import { AggregateSeries } from "../trendSeries";
import { TrendsState } from "../useTenantTrends";
import BacklogChart from "./BacklogChart";
import ThroughputChart from "./ThroughputChart";

interface iTrendBandProps {
    data: AggregateSeries;
    state: TrendsState;
}

/**
 * States the population the aggregate was summed over.
 *
 * A range rather than a single count, because the population varies bucket to
 * bucket (spec §2.7). A single figure would imply a stability the line does
 * not have. When it happens not to vary, say so plainly rather than printing
 * "40–40".
 */
function coverageNote(data: AggregateSeries): string {
    const span = data.minTenants === data.maxTenants ? `${data.minTenants}` : `${data.minTenants}–${data.maxTenants}`;
    return `summed across ${span} of ${data.total} tenants in this window`;
}

/**
 * The two aggregate charts, side by side above the table.
 *
 * Owns the not-yet and cannot-read states so the page shell does not have to.
 * Both matter: an empty chart during load reads as "there is no history",
 * which is a different statement from "we have not fetched it yet", and empty
 * axes after a total failure are the chart equivalent of a blank table
 * standing in for an error.
 */
function TrendBand({ data, state }: iTrendBandProps) {
    /*
        Collapsing `idle` into `loading` is safe, but only because of what
        those two states mean in the hook — worth stating, because an earlier
        draft made this branch permanent for a zero-tenant roster.

        `idle` means "no token", and the page shell renders this band only once
        an overview has loaded, which requires one. A token with an EMPTY
        roster is not `idle` — the hook reports `ready`, because there is
        nothing to fetch and nothing to show, and the charts then say "No
        history to chart yet" rather than claiming to still be loading. That is
        also what keeps this band consistent with the table's own empty state
        directly below it.
    */
    if (state === "idle" || state === "loading") {
        return <div className="mp-trend-note">Loading the last 24 hours…</div>;
    }

    if (state === "failed") {
        return (
            <div className="mp-trend-note">
                No tenant&rsquo;s history could be read. The table above is unaffected — this is the history store, not
                the live figures.
            </div>
        );
    }

    if (state !== "ready") {
        // Exhaustiveness guard. TypeScript's `never` here means a future
        // TrendsState member (Spec 2's verdict will very likely add one) forces
        // this file to be revisited rather than silently rendering the charts
        // with whatever data was passed for a state neither this file nor its
        // reader intended.
        const _exhaustive: never = state;
        return null;
    }

    return (
        <div className="mp-trend-band">
            <div className="mp-trend-charts">
                <div className="mp-trend-cell">
                    <div className="mp-trend-label">Throughput 24h</div>
                    <ThroughputChart data={data} />
                </div>
                <div className="mp-trend-cell">
                    <div className="mp-trend-label">Backlog 24h</div>
                    <BacklogChart data={data} />
                </div>
            </div>
            {/*
                One note for the band, not one per chart. Both charts are summed
                over the same population, so printing the figure twice is noise —
                and a duplicated node makes every getByText on it ambiguous.
            */}
            <div className="mp-trend-coverage">{coverageNote(data)}</div>
        </div>
    );
}

export default TrendBand;
